/* ==========================================================================
   Filament365 — global.js
   Ortak yardımcılar, overlay (çekmece/modal) altyapısı, header davranışları,
   mega menü, mobil menü, duyuru çubuğu, footer akordeonu, miktar girişi,
   giriş animasyonları ve ekran okuyucu duyuruları.
   Vanilla JS — dış bağımlılık yok.
   ========================================================================== */
(function () {
  'use strict';

  var S = window.themeStrings || {};
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var FOCUSABLE = [
    'a[href]', 'button:not([disabled])', 'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])', 'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])',
    'summary'
  ].join(',');

  /* ---------- Yardımcılar ---------- */

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function getFocusable(container) {
    return Array.prototype.filter.call(container.querySelectorAll(FOCUSABLE), function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
  }

  var scrollLocks = 0;
  function lockScroll() {
    scrollLocks++;
    if (scrollLocks === 1) {
      var sbw = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.paddingRight = sbw > 0 ? sbw + 'px' : '';
      document.body.classList.add('is-locked');
    }
  }
  function unlockScroll() {
    scrollLocks = Math.max(0, scrollLocks - 1);
    if (scrollLocks === 0) {
      document.body.classList.remove('is-locked');
      document.body.style.paddingRight = '';
    }
  }

  var announcer;
  function announce(message) {
    if (!announcer) {
      announcer = document.createElement('div');
      announcer.className = 'visually-hidden';
      announcer.setAttribute('aria-live', 'polite');
      announcer.setAttribute('role', 'status');
      document.body.appendChild(announcer);
    }
    announcer.textContent = '';
    setTimeout(function () { announcer.textContent = message; }, 60);
  }

  function toast(message, type) {
    var region = document.getElementById('ToastRegion');
    if (!region || !message) return;
    var el = document.createElement('div');
    el.className = 'toast' + (type === 'error' ? ' toast--error' : '');
    var icon = type === 'error'
      ? '<svg class="icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 7.5v5.5M12 16.5v.01"/></svg>'
      : '<svg class="icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m5 12.5 4.5 4.5L19 7.5"/></svg>';
    el.innerHTML = icon + '<span></span>';
    el.querySelector('span').textContent = message;
    region.appendChild(el);
    setTimeout(function () {
      el.classList.add('is-leaving');
      setTimeout(function () { el.remove(); }, 260);
    }, type === 'error' ? 5000 : 3200);
  }

  window.F365 = {
    debounce: debounce,
    getFocusable: getFocusable,
    lockScroll: lockScroll,
    unlockScroll: unlockScroll,
    announce: announce,
    toast: toast,
    reducedMotion: reducedMotion
  };

  /* ---------- Overlay tabanı (çekmece + modal) ---------- */

  class OverlayElement extends HTMLElement {
    constructor() {
      super();
      this._opener = null;
      this._onKeydown = this._onKeydown.bind(this);
      this.addEventListener('click', function (e) {
        if (e.target.closest('[data-close]')) {
          e.preventDefault();
          this.close();
        }
      }.bind(this));
    }

    get isOpen() { return this.classList.contains('is-open'); }

    get panel() { return this.querySelector('[role="dialog"]'); }

    open(opener) {
      if (this.isOpen) return;
      this._opener = opener || document.activeElement;
      this.hidden = false;
      // reflow -> geçiş animasyonu için
      void this.offsetWidth;
      this.classList.add('is-open');
      lockScroll();
      document.addEventListener('keydown', this._onKeydown);
      var panel = this.panel;
      if (panel) {
        setTimeout(function () {
          var first = panel.querySelector('[data-autofocus]');
          (first || panel).focus({ preventScroll: true });
        }, 30);
      }
      this.dispatchEvent(new CustomEvent('overlay:open', { bubbles: true }));
    }

    close() {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      unlockScroll();
      document.removeEventListener('keydown', this._onKeydown);
      var self = this;
      var done = function () {
        if (!self.isOpen) self.hidden = true;
      };
      if (reducedMotion) done(); else setTimeout(done, 320);
      if (this._opener && typeof this._opener.focus === 'function' && document.contains(this._opener)) {
        this._opener.focus({ preventScroll: true });
      }
      this.dispatchEvent(new CustomEvent('overlay:close', { bubbles: true }));
    }

    _onKeydown(e) {
      if (e.key === 'Escape') {
        // Üstte başka bir overlay (ör. hızlı görünüm) açıksa sadece o kapansın
        var openOverlays = document.querySelectorAll('.drawer.is-open, .modal.is-open');
        if (openOverlays[openOverlays.length - 1] !== this && openOverlays.length > 1) return;
        e.stopPropagation();
        this.close();
        return;
      }
      if (e.key !== 'Tab') return;
      var panel = this.panel;
      if (!panel) return;
      var items = getFocusable(panel);
      if (!items.length) { e.preventDefault(); panel.focus(); return; }
      var first = items[0];
      var last = items[items.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }
  window.OverlayElement = OverlayElement;

  /* ---------- Mobil menü ---------- */

  class MobileMenu extends OverlayElement {
    connectedCallback() {
      var self = this;
      document.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-open-menu]');
        if (!btn) return;
        e.preventDefault();
        btn.setAttribute('aria-expanded', 'true');
        self.open(btn);
      });
      this.addEventListener('overlay:close', function () {
        document.querySelectorAll('[data-open-menu]').forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
      });
      // Menü bağlantısına tıklanınca kapat (aynı sayfa içi # bağlantıları için)
      this.addEventListener('click', function (e) {
        var a = e.target.closest('a[href]');
        if (a && a.getAttribute('href').charAt(0) === '#') self.close();
      });
      // Masaüstüne geçilirse kapat
      window.matchMedia('(min-width: 990px)').addEventListener('change', function (mq) {
        if (mq.matches) self.close();
      });
    }
  }
  customElements.define('mobile-menu', MobileMenu);

  /* ---------- Sabit header ---------- */

  class StickyHeader extends HTMLElement {
    connectedCallback() {
      var self = this;
      var ticking = false;
      this._onScroll = function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () {
          self.classList.toggle('is-scrolled', window.scrollY > 24);
          ticking = false;
        });
      };
      if (this.dataset.sticky === 'true') this.classList.add('is-sticky');
      window.addEventListener('scroll', this._onScroll, { passive: true });
      this._onScroll();

      var header = this.querySelector('.header');
      if (header && 'ResizeObserver' in window) {
        new ResizeObserver(function () {
          document.documentElement.style.setProperty('--header-height', header.offsetHeight + 'px');
        }).observe(header);
      }
    }
    disconnectedCallback() {
      window.removeEventListener('scroll', this._onScroll);
    }
  }
  customElements.define('sticky-header', StickyHeader);

  /* ---------- Mega menü ---------- */

  class MegaMenuItem extends HTMLElement {
    connectedCallback() {
      var self = this;
      this.toggle = this.querySelector('.nav-toggle');
      this.panel = this.querySelector('.mega');
      this.closeTimer = null;
      if (!this.toggle || !this.panel) return;

      this.toggle.addEventListener('click', function () {
        self.isOpen ? self.close() : self.open();
      });

      var canHover = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.addEventListener('mouseenter', function () {
        if (!canHover.matches) return;
        clearTimeout(self.closeTimer);
        self.open();
      });
      this.addEventListener('mouseleave', function () {
        if (!canHover.matches) return;
        self.closeTimer = setTimeout(function () { self.close(); }, 140);
      });

      this.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && self.isOpen) {
          self.close();
          self.toggle.focus();
        }
      });
      this.addEventListener('focusout', function (e) {
        if (!self.contains(e.relatedTarget)) self.close();
      });
      document.addEventListener('click', function (e) {
        if (self.isOpen && !self.contains(e.target)) self.close();
      });
    }

    get isOpen() { return this.toggle.getAttribute('aria-expanded') === 'true'; }

    open() {
      document.querySelectorAll('mega-menu-item').forEach(function (m) { if (m !== this && m.close) m.close(); }, this);
      this.panel.hidden = false;
      void this.panel.offsetWidth;
      this.panel.classList.add('is-open');
      this.toggle.setAttribute('aria-expanded', 'true');
    }

    close() {
      if (!this.toggle || !this.isOpen) return;
      this.panel.classList.remove('is-open');
      this.toggle.setAttribute('aria-expanded', 'false');
      var panel = this.panel;
      var self = this;
      setTimeout(function () { if (!self.isOpen) panel.hidden = true; }, reducedMotion ? 0 : 180);
    }
  }
  customElements.define('mega-menu-item', MegaMenuItem);

  /* ---------- Duyuru çubuğu ---------- */

  class AnnouncementSlider extends HTMLElement {
    connectedCallback() {
      var self = this;
      this.items = Array.prototype.slice.call(this.querySelectorAll('.announcement-bar__item'));
      if (this.items.length < 2) return;
      this.index = 0;
      this.paused = false;

      var prev = this.querySelector('[data-prev]');
      var next = this.querySelector('[data-next]');
      if (prev) prev.addEventListener('click', function () { self.go(self.index - 1); });
      if (next) next.addEventListener('click', function () { self.go(self.index + 1); });

      this.addEventListener('mouseenter', function () { self.paused = true; });
      this.addEventListener('mouseleave', function () { self.paused = false; });
      this.addEventListener('focusin', function () { self.paused = true; });
      this.addEventListener('focusout', function () { self.paused = false; });

      if (this.dataset.autoplay === 'true' && !reducedMotion) {
        var speed = parseInt(this.dataset.speed, 10) || 5000;
        this.timer = setInterval(function () {
          if (!self.paused && !document.hidden) self.go(self.index + 1);
        }, speed);
      }
    }
    go(i) {
      var n = this.items.length;
      this.items[this.index].classList.remove('is-active');
      this.index = (i + n) % n;
      this.items[this.index].classList.add('is-active');
    }
    disconnectedCallback() { clearInterval(this.timer); }
  }
  customElements.define('announcement-slider', AnnouncementSlider);

  /* ---------- Footer akordeonu (yalnızca mobilde kapanır) ---------- */

  class FooterAccordion extends HTMLElement {
    connectedCallback() {
      var details = this.querySelector('details');
      if (!details) return;
      var mq = window.matchMedia('(min-width: 750px)');
      var sync = function () { details.open = mq.matches; };
      sync();
      mq.addEventListener('change', sync);
      var summary = details.querySelector('summary');
      if (summary) {
        summary.addEventListener('click', function (e) {
          if (mq.matches) e.preventDefault();
        });
      }
    }
  }
  customElements.define('footer-accordion', FooterAccordion);

  /* ---------- Miktar girişi ---------- */

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      var self = this;
      this.input = this.querySelector('input');
      if (!this.input) return;
      var minus = this.querySelector('[data-qty-minus]');
      var plus = this.querySelector('[data-qty-plus]');
      if (minus) minus.addEventListener('click', function () { self.step(-1); });
      if (plus) plus.addEventListener('click', function () { self.step(1); });
    }
    step(dir) {
      var input = this.input;
      var min = input.min !== '' ? parseInt(input.min, 10) : 1;
      var max = input.max !== '' ? parseInt(input.max, 10) : Infinity;
      var current = parseInt(input.value, 10);
      if (isNaN(current)) current = min;
      var next = current + dir;
      if (next < min) next = min;
      if (next > max) {
        this.dispatchEvent(new CustomEvent('qty:max', { bubbles: true, detail: { max: max, input: input } }));
        return;
      }
      if (next === current) return;
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ---------- Giriş animasyonları ---------- */

  var revealObserver = null;
  function initReveal(root) {
    var els = (root || document).querySelectorAll('.reveal:not(.is-visible)');
    if (!els.length) return;
    if (reducedMotion || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            revealObserver.unobserve(entry.target);
          }
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    }
    els.forEach(function (el) { revealObserver.observe(el); });
  }

  document.addEventListener('DOMContentLoaded', function () { initReveal(); });
  document.addEventListener('shopify:section:load', function (e) {
    initReveal(e.target);
    // Tema düzenleyicide yeniden yüklenen bölümlerdeki öğeler hemen görünsün
    e.target.querySelectorAll('.reveal').forEach(function (el) { el.classList.add('is-visible'); });
  });
  window.F365.initReveal = initReveal;

  /* ---------- Bölüm içi ürün kaydırıcıları (ok düğmeleri) ---------- */

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-scroll-dir]');
    if (!btn) return;
    var target = document.getElementById(btn.getAttribute('aria-controls'));
    if (!target) return;
    var dir = btn.getAttribute('data-scroll-dir') === 'next' ? 1 : -1;
    target.scrollBy({ left: dir * target.clientWidth * 0.85, behavior: reducedMotion ? 'auto' : 'smooth' });
  });
})();
